// @ts-ignore: no type decl for this library
import * as jedLib from "jed";

// Keep translations usable before an application installs its catalog.  This
// matters for shared components that can render while providers are still
// waiting for configuration data.
export let jed: any = new jedLib.Jed({});

let activeLanguage = "en";

/**
 * Set up jed library for internationalization,
 * based on browser language settings.
 */
export function setupI18n(lang: string, strings: { [s: string]: any }): void {
  lang = lang.replaceAll("_", "-");
  activeLanguage = lang;
  const normalizedLanguage = lang.toLowerCase();
  const baseLanguage = normalizedLanguage.split("-", 1)[0];
  const normalizedCatalogs = new Map(
    Object.entries(strings).map(([language, catalog]) => [
      language.replaceAll("_", "-").toLowerCase(),
      catalog,
    ]),
  );
  const catalog =
    normalizedCatalogs.get(normalizedLanguage) ??
    normalizedCatalogs.get(baseLanguage) ??
    {};
  jed = new jedLib.Jed(catalog);
}

/**
 * Use different translations for testing.  Should not be used outside
 * of test cases.
 */
export function internalSetStrings(langStrings: any): void {
  jed = new jedLib.Jed(langStrings);
}

declare const __translated: unique symbol;
export type TranslatedString = string & { [__translated]: true };

/**
 * Convert template strings to a msgid
 */
function toI18nString(stringSeq: ReadonlyArray<string>): TranslatedString {
  let s = "";
  for (let i = 0; i < stringSeq.length; i++) {
    s += stringSeq[i];
    if (i < stringSeq.length - 1) {
      s += `%${i + 1}$s`;
    }
  }
  return s as TranslatedString;
}

export type FixedTranslations = Readonly<
  { en: string } & Record<string, string>
>;

function substituteSerializedValues(
  translation: string,
  values: readonly any[],
): TranslatedString {
  return translation.replace(/%(\d+)\$s/g, (_match, index: string) =>
    String(values[Number.parseInt(index, 10) - 1]),
  ) as TranslatedString;
}

function fixedTranslation(
  translations: FixedTranslations,
  allowCatalogFallback: boolean,
  values: readonly any[],
): TranslatedString {
  const normalizedTranslations = new Map(
    Object.entries(translations).map(([language, translation]) => [
      language.replaceAll("_", "-").toLowerCase(),
      translation,
    ]),
  );
  const source = translations.en;
  const normalizedLanguage = activeLanguage.toLowerCase();
  const baseLanguage = normalizedLanguage.split("-", 1)[0];

  // The source language is itself fixed and must never be overridden by an
  // English catalogue entry.
  if (baseLanguage === "en") {
    return substituteSerializedValues(source, values);
  }

  const prescribed =
    normalizedTranslations.get(normalizedLanguage) ??
    normalizedTranslations.get(baseLanguage);
  if (prescribed !== undefined) {
    return substituteSerializedValues(prescribed, values);
  }

  if (allowCatalogFallback) {
    return translateSerialized(source, values);
  }
  return substituteSerializedValues(source, values);
}

/**
 * Translate prescribed text whose authoritative translations live in source.
 * Languages without a fixed translation continue through the gettext
 * catalogue and ultimately fall back to the prescribed English source.
 */
export function fixed(
  translations: FixedTranslations,
  ...values: any[]
): TranslatedString {
  return fixedTranslation(translations, true, values);
}

/**
 * Translate prescribed text using only source-controlled translations.
 */
export function fixedOnly(
  translations: FixedTranslations,
  ...values: any[]
): TranslatedString {
  return fixedTranslation(translations, false, values);
}

function translateSerialized(
  source: string,
  values: readonly any[],
): TranslatedString {
  // jed throws an Error when the key is empty.
  if (!source) return "" as TranslatedString;
  return jed
    .translate(source)
    .ifPlural(1, source)
    .fetch(...values);
}

/**
 * Internationalize a string template with arbitrary serialized values.
 */
export function singular(
  stringSeq: TemplateStringsArray,
  ...values: any[]
): TranslatedString {
  const s = toI18nString(stringSeq);
  return translateSerialized(s, values);
}

function withContext(ctx: string): typeof singular {
  return function (t: TemplateStringsArray, ...v: any[]): TranslatedString {
    const s = toI18nString(t);
    const tr = jed
      .translate(s)
      .withContext(ctx)
      .ifPlural(1, s)
      .fetch(...v);
    return tr;
  };
}

/**
 * Internationalize a string template without serializing
 */
export function translate(
  stringSeq: TemplateStringsArray,
  ...values: any[]
): TranslatedString[] {
  const s = toI18nString(stringSeq);
  if (!s) return [];
  const translation: TranslatedString = jed.ngettext(s, s, 1);
  return replacePlaceholderWithValues(translation, values);
}

/**
 * Internationalize a string template without serializing
 */
export function Translate({
  children,
  debug,
  context: ctx,
}: {
  children: any;
  debug?: boolean;
  context?: string;
}): any {
  const c = [].concat(children);
  const s = stringifyArray(c);
  if (!s) return [];
  const translation: TranslatedString = ctx
    ? jed.npgettext(ctx, s, s, 1)
    : jed.ngettext(s, s, 1);
  if (debug) {
    console.log("looking for ", s, "got", translation);
  }
  return replacePlaceholderWithValues(translation, c);
}

function replacePlaceholderWithValues(
  translation: TranslatedString,
  childArray: Array<any>,
): Array<any> {
  const tr = translation.split(/%(\d+)\$s/);
  // const childArray = toChildArray(children);
  // Merge consecutive string children.
  const placeholderChildren = [];
  for (let i = 0; i < childArray.length; i++) {
    const x = childArray[i];
    if (x === undefined) {
      continue;
    } else if (typeof x === "string") {
      continue;
    } else {
      placeholderChildren.push(x);
    }
  }
  const result = [];
  for (let i = 0; i < tr.length; i++) {
    if (i % 2 == 0) {
      // Text
      result.push(tr[i]);
    } else {
      const childIdx = Number.parseInt(tr[i]) - 1;
      result.push(placeholderChildren[childIdx]);
    }
  }
  return result;
}

function stringifyArray(children: Array<any>): string {
  let n = 1;
  const ss = children.map((c) => {
    if (typeof c === "string") {
      return c;
    }
    return `%${n++}$s`;
  });
  const s = ss.join("").replace(/ +/g, " ").trim();
  return s;
}

export type InternationalizationAPI = typeof i18n;
export type Translator = (i18n: InternationalizationAPI) => TranslatedString;

export const i18n = {
  str: singular,
  fixed,
  fixedOnly,
  ctx: withContext,
  singular,
  Translate,
  translate,
};
